import React, { createContext, useContext, useState, useEffect } from 'react';
import aiQueue from '../utils/aiQueue';

const QueueContext = createContext();

export function QueueProvider({ children }) {
  const [queueStats, setQueueStats] = useState(null);
  const [myJobs, setMyJobs] = useState([]);
  const [activeJobs, setActiveJobs] = useState(new Map());
  const [loading, setLoading] = useState(false);

  // Load queue statistics
  const loadQueueStats = async () => {
    try {
      const stats = await aiQueue.getQueueStats();
      setQueueStats(stats);
    } catch (error) {
      // Set fallback stats if error still occurs
      console.warn('Failed to load queue stats, using fallback:', error);
      setQueueStats({
        queue: {
          waiting: 0,
          active: 0,
          completed: 0,
          failed: 0,
          delayed: 0,
          total: 0
        },
        performance: {
          avgProcessingTime: 0,
          successRate: 100
        },
        timestamp: Date.now(),
        note: 'Queue system offline'
      });
    }
  };

  // Load user's recent jobs
  const loadMyJobs = async () => {
    try {
      const result = await aiQueue.getMyJobs(20, 0);
      setMyJobs(result.jobs || []);
    } catch (error) {
      // This catch block is now mostly redundant since getMyJobs returns fallback data,
      // but kept for unexpected errors
      console.warn('Failed to load user jobs (using fallback):', error);
      setMyJobs([]);
    }
  };

  // Submit AI job with progress tracking
  const submitJob = async (type, data, onProgress) => {
    setLoading(true);
    
    try {
      const job = await aiQueue.submitJob(type, data);
      
      // Track active job
      setActiveJobs(prev => new Map(prev).set(job.jobId, {
        type,
        data,
        status: 'submitted',
        timestamp: Date.now()
      }));

      // Start polling for completion
      aiQueue.pollJobStatus(
        job.jobId,
        (status) => {
          // Update job status
          setActiveJobs(prev => {
            const updated = new Map(prev);
            updated.set(job.jobId, {
              ...updated.get(job.jobId),
              status: status.state,
              progress: status.progress,
              estimatedWait: status.estimatedWait
            });
            return updated;
          });

          // Call progress callback
          if (onProgress) {
            onProgress(status);
          }
        },
        (result) => {
          // Job completed successfully
          setActiveJobs(prev => {
            const updated = new Map(prev);
            updated.delete(job.jobId);
            return updated;
          });
          
          // Reload user jobs
          loadMyJobs();
          
          return result;
        },
        (error) => {
          // Job failed
          setActiveJobs(prev => {
            const updated = new Map(prev);
            updated.delete(job.jobId);
            return updated;
          });
          
          throw error;
        }
      );

      return job;
    } finally {
      setLoading(false);
    }
  };

  // Generate appreciation
  const generateAppreciation = async (data, onProgress) => {
    return submitJob('generate-appreciation', data, onProgress);
  };

  // Generate scene
  const generateScene = async (data, onProgress) => {
    return submitJob('generate-scene', data, onProgress);
  };

  // Ask mentor
  const askMentor = async (data, onProgress) => {
    return submitJob('ask-mentor', data, onProgress);
  };

  // Cancel job
  const cancelJob = async (jobId) => {
    try {
      await aiQueue.cancelJob(jobId);
      
      // Remove from active jobs
      setActiveJobs(prev => {
        const updated = new Map(prev);
        updated.delete(jobId);
        return updated;
      });
      
      // Reload user jobs
      loadMyJobs();
      
      return true;
    } catch (error) {
      console.error('Failed to cancel job:', error);
      throw error;
    }
  };

  // Get job status
  const getJobStatus = async (jobId) => {
    try {
      return await aiQueue.getJobStatus(jobId);
    } catch (error) {
      console.error('Failed to get job status:', error);
      throw error;
    }
  };

  // Check queue health
  const checkHealth = async () => {
    try {
      return await aiQueue.checkHealth();
    } catch (error) {
      console.error('Failed to check queue health:', error);
      throw error;
    }
  };

  // Load initial data
  useEffect(() => {
    loadQueueStats();
    loadMyJobs();
    
    // Refresh stats every 30 seconds
    const statsInterval = setInterval(loadQueueStats, 30000);
    
    return () => {
      clearInterval(statsInterval);
      aiQueue.stopAllPolling();
    };
  }, []);

  const value = {
    // State
    queueStats,
    myJobs,
    activeJobs,
    loading,
    
    // Actions
    submitJob,
    generateAppreciation,
    generateScene,
    askMentor,
    cancelJob,
    getJobStatus,
    checkHealth,
    loadQueueStats,
    loadMyJobs,
    
    // Computed values
    hasActiveJobs: activeJobs.size > 0,
    activeJobCount: activeJobs.size,
    queueHealth: queueStats?.performance?.successRate || 0
  };

  return (
    <QueueContext.Provider value={value}>
      {children}
    </QueueContext.Provider>
  );
}

// Custom hook for using queue context
export function useQueue() {
  const context = useContext(QueueContext);
  if (!context) {
    throw new Error('useQueue must be used within a QueueProvider');
  }
  return context;
} 